import { forwardRef, useEffect, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { Icon } from '../../icons/Icon';
import { Kbd } from '../../primitives/Kbd/Kbd';
import { useOptionalAppShell } from '../AppShell/AppShellContext';
import { useDsT } from '../../services/i18n';

export interface TopBarProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  /** Menu toggle handler; defaults to the surrounding AppShell's `toggleNav`. */
  onMenuToggle?: () => void;
  menuLabel?: string;
  hideMenuToggle?: boolean;
  /**
   * `mobile` (default) shows the hamburger only while the surrounding AppShell is in its off-canvas mode —
   * on desktop the rail carries its own collapse control. Outside an AppShell the toggle shows whenever
   * `onMenuToggle` is given. `always` shows it at every width.
   */
  menuToggle?: 'always' | 'mobile';
  /** Optional context on the left, after the hamburger. */
  title?: ReactNode;
  breadcrumb?: ReactNode;
  start?: ReactNode;
  /** Replaces the search trigger. */
  center?: ReactNode;
  onSearch?: () => void;
  /** Accessible name and visible placeholder of the search trigger. */
  searchPlaceholder?: string;
  /** Visible label of the search trigger when it differs from its accessible name (`searchPlaceholder`). */
  searchLabel?: ReactNode;
  /** Registers Cmd/Ctrl+K to call `onSearch` (off when the host's hotkey service owns it). */
  searchHotkey?: boolean;
  /**
   * W4 (Zagru Flow): the workspace control at the start of the bar (`WorkspaceSwitcher`). It keeps its own width —
   * only the tenant name inside it truncates — and the search takes what is left.
   */
  workspace?: ReactNode;
  /** @deprecated W4: pass the workspace control as `workspace`. Kept for hosts still using `ScopeSwitcher` on the right. */
  orgSwitcher?: ReactNode;
  /** The `+` create menu (`CreateMenu`). */
  createMenu?: ReactNode;
  /** The bell (`NotificationsButton`). */
  notifications?: ReactNode;
  /** The `?` menu (`HelpMenu`). */
  help?: ReactNode;
  /** Avatar menu (`UserMenu`). */
  userMenu?: ReactNode;
  /** Extra actions rendered before the org switcher. */
  actions?: ReactNode;
}

export const isApplePlatform = (): boolean =>
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || '');

export const topBarIconButton =
  'relative inline-flex h-control-sm w-control-sm min-h-6 min-w-6 shrink-0 items-center justify-center rounded-md text-fg-muted transition-colors motion-fast hover:bg-hover hover:text-fg focus-ring';

/**
 * The 64px application header: a hamburger below the AppShell's breakpoint, the global search trigger in
 * the middle (a button that opens the command palette — never an input), and on the right the org
 * switcher, create menu, notifications, help and user menu slots, each a composable part from
 * `layout/TopBar/parts`.
 */
export const TopBar = forwardRef<HTMLElement, TopBarProps>(function TopBar(
  {
    onMenuToggle,
    menuLabel: menuLabelProp,
    hideMenuToggle = false,
    menuToggle = 'mobile',
    title,
    breadcrumb,
    start,
    center,
    onSearch,
    searchPlaceholder: searchPlaceholderProp,
    searchLabel,
    searchHotkey = true,
    workspace,
    orgSwitcher,
    createMenu,
    notifications,
    help,
    userMenu,
    actions,
    className,
    ...rest
  },
  ref,
) {
  const t = useDsT();
  const menuLabel = menuLabelProp ?? t('ds.topBar.openNavigation', 'Open navigation');
  const searchPlaceholder = searchPlaceholderProp ?? t('ds.topBar.searchPlaceholder', 'Search shipments, orders, warehouses, containers…');
  const shell = useOptionalAppShell();
  const toggleNav = onMenuToggle ?? shell?.toggleNav;
  const mobile = shell ? shell.isMobile : true;
  const showMenuToggle = !hideMenuToggle && !!toggleNav && (menuToggle === 'always' || mobile);
  const apple = isApplePlatform();

  useEffect(() => {
    if (!searchHotkey || !onSearch) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === 'k' || e.key === 'K') && (apple ? e.metaKey : e.ctrlKey) && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        onSearch();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [searchHotkey, onSearch, apple]);

  return (
    <header
      ref={ref}
      className={cn('zo-top-bar sticky top-0 z-header flex h-topbar shrink-0 items-center gap-2 border-b border-border bg-surface-1 px-3 text-fg sm:gap-3 sm:px-4', className)}
      {...rest}
    >
      {/* Below `sm` the three regions stop sharing the width: the search collapses to an icon and every control keeps its size. */}
      <div className={cn('flex min-w-0 items-center gap-2', workspace != null ? 'shrink-0' : 'max-sm:flex-none sm:flex-1')}>
        {showMenuToggle ? (
          <button type="button" onClick={toggleNav} aria-label={menuLabel} className={topBarIconButton}>
            <Icon name="menu" size={20} />
          </button>
        ) : null}
        {breadcrumb != null || title != null ? (
          <div className="flex min-w-0 flex-col justify-center leading-tight">
            {breadcrumb != null ? <div className="truncate type-caption text-fg-subtle">{breadcrumb}</div> : null}
            {title != null ? <div className="truncate type-body-strong">{title}</div> : null}
          </div>
        ) : null}
        {start}
        {workspace != null ? (
          <div data-slot="workspace" className="flex min-w-0 max-w-[20rem] items-center max-sm:max-w-[12rem] xl:max-w-[24rem]">
            {workspace}
          </div>
        ) : null}
      </div>

      <div className={cn('flex min-w-0 items-center max-sm:flex-none', workspace != null ? 'flex-1 justify-start sm:ps-1' : 'justify-center sm:flex-[2]')}>
        {center !== undefined ? (
          center
        ) : (
          <button
            type="button"
            onClick={onSearch}
            aria-label={searchPlaceholder}
            aria-keyshortcuts={apple ? 'Meta+K' : 'Control+K'}
            className="zo-top-bar__search flex h-control-sm w-full max-w-[640px] items-center gap-2 rounded-md border border-border bg-surface-0 px-3 text-start type-body text-fg-muted transition-colors motion-fast hover:border-border-strong hover:text-fg focus-ring max-sm:w-control-sm max-sm:justify-center max-sm:px-0"
          >
            <Icon name="search" size={16} className="shrink-0 text-fg-subtle" />
            <span className="min-w-0 flex-1 truncate max-sm:hidden">{searchLabel ?? searchPlaceholder}</span>
            {/* Kbd sets its own display, so the breakpoint lives on a wrapper. */}
            <span className="hidden shrink-0 sm:inline-flex">
              <Kbd keys="mod+k" size="sm" />
            </span>
          </button>
        )}
      </div>

      <div className={cn('ms-auto flex shrink-0 items-center justify-end gap-1', workspace == null && 'sm:min-w-0 sm:flex-1')}>
        {actions}
        {orgSwitcher != null ? <div data-slot="org-switcher" className="flex items-center max-sm:shrink-0 sm:min-w-0">{orgSwitcher}</div> : null}
        {createMenu != null ? <div data-slot="create-menu" className="flex items-center">{createMenu}</div> : null}
        {notifications != null ? <div data-slot="notifications" className="flex items-center">{notifications}</div> : null}
        {help != null ? <div data-slot="help" className={cn('flex items-center', workspace != null && 'max-sm:hidden')}>{help}</div> : null}
        {userMenu != null ? <div data-slot="user-menu" className="ms-1 flex shrink-0 items-center">{userMenu}</div> : null}
      </div>
    </header>
  );
});
